import * as yup from "yup";

import {
  BoxProps,
  Button,
  ButtonProps,
  Divider,
  Flex,
  Image,
  LinkProps,
  Text,
  TextProps,
} from "@chakra-ui/react";
import { AppForm, FormField, FormFieldProps, Link } from "@picovico/ui";
import { useMemo } from "react";
import { useRegisterUserMutation } from "store/services/auth";

import { useAppToast } from "@/hooks/useAppToast";
import { useSocialSignIn } from "@/hooks/useSocialSignIn";
import { useVisibleSocialProviders } from "@/hooks/useWebFeature";
import { getPageRoutes } from "@/routes/pageRoutes";
import { resetUserStateAction } from "@/store/slices";
import {
  catchErrorHandler,
  parseApiError,
  storeLocalStorageData,
} from "@picovico/utils";
import { useRouter } from "next/router";
import { useDispatch } from "react-redux";

export interface registerValues {
  name: string;
  email: string;
  mobile: string;
  // password: string;
  // confirm_password: string;
  [key: string]: string;
}

export type ExtraFieldConfig = FormFieldProps;

const registerFormValidationSchema = yup.object().shape({
  name: yup.string().required().label("Name"),
  email: yup.string().email().required().label("Email"),
  mobile: yup
    .string()
    .matches(
      /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/,
      "Mobile number is not valid",
    )
    .length(10, "Mobile number should be 10 digits"),
  // password: yup.string().required().label("Password"),
  // confirm_password: yup
  //   .string()
  //   .oneOf(
  //     [yup.ref("password")],
  //     "Confirm password doesn't match with password",
  //   )
  //   .label("Confirm Password"),
});

const initialValues: registerValues = {
  name: "",
  email: "",
  mobile: "",
  // password: "",
  // confirm_password: "",
};

type SocialAuthProvider = { providerName: string; providerSvg: string };

interface RegisterFormProps extends BoxProps {
  socialAuthProviders?: SocialAuthProvider[];
  buttonProps?: ButtonProps;
  formProps?: BoxProps;
  formFieldProps?: FormFieldProps;
  extraFields?: ExtraFieldConfig[];
  nameFieldProps?: FormFieldProps;
  emailFieldProps?: FormFieldProps;
  mobileFieldProps?: FormFieldProps;
  passwordNoteProps?: TextProps;
  loginLinkTextProps?: TextProps;
  loginLinkProps?: LinkProps;
  hideLoginLink?: boolean;
  socialButtonProps?: ButtonProps;
  showSocialProviderLabel?: boolean;
}

function RegisterForm({
  socialAuthProviders: configuredSocialProviders,
  formFieldProps,
  buttonProps,
  formProps,
  extraFields,
  nameFieldProps,
  emailFieldProps,
  mobileFieldProps,
  passwordNoteProps,
  loginLinkTextProps,
  loginLinkProps,
  hideLoginLink = false,
  socialButtonProps,
  showSocialProviderLabel = false,
  ...props
}: RegisterFormProps) {
  const router = useRouter();
  const dispatch = useDispatch();
  const { error: errorToast, success } = useAppToast();
  const [register, { isLoading, error }] = useRegisterUserMutation({});
  const startSocialSignIn = useSocialSignIn();
  // Google is hidden while the company has google_signin off.
  const socialAuthProviders = useVisibleSocialProviders(
    configuredSocialProviders,
  );

  const mergedInitialValues = useMemo(() => {
    if (!extraFields?.length) return initialValues;
    const extra = extraFields.reduce<Record<string, string>>(
      (acc, { name }) => {
        acc[name] = "";
        return acc;
      },
      {},
    );
    return { ...initialValues, ...extra };
  }, [extraFields]);

  const mergedValidationSchema = useMemo(() => {
    if (!extraFields?.length) return registerFormValidationSchema;
    const extraShape: Record<string, yup.AnySchema> = {};
    extraFields.forEach(({ name, label, required }) => {
      let schema: yup.AnySchema = yup.string();
      if (required) schema = (schema as yup.StringSchema).required();
      if (label) schema = schema.label(label);
      extraShape[name] = schema;
    });
    return registerFormValidationSchema.concat(yup.object().shape(extraShape));
  }, [extraFields]);

  return (
    <AppForm<registerValues>
      apiError={(error && parseApiError(error)) || null}
      formProps={{
        display: "flex",
        flexDirection: "column",
        alignItems: "start",
        justifyContent: "center",
        gap: "1rem",
        mt: "1.5rem",
        ...props,
        ...formProps,
      }}
      initialValues={mergedInitialValues}
      validationSchema={mergedValidationSchema}
      onSubmit={(values) => {
        register(values)
          .unwrap()
          .then((res) => {
            if (res?.data.token) {
              storeLocalStorageData("token", res.data.token);
              storeLocalStorageData("device_id", res.data.device_id);
            }
            res?.data?.message && success(res?.data?.message);
            dispatch(resetUserStateAction());
            const nextUrl = router.query.next as string;
            if (nextUrl) {
              const target = decodeURIComponent(nextUrl);
              if (target.startsWith("/")) {
                router.push(target);
              } else {
                router.push(getPageRoutes(target));
              }
            } else {
              router.push(getPageRoutes("home"));
            }
          })
          .catch((e) => {
            catchErrorHandler(e, errorToast);
          });
      }}
    >
      <FormField name="name" label="Name" required {...formFieldProps} {...nameFieldProps} />
      <FormField name="email" label="Email address" required {...formFieldProps} {...emailFieldProps} />
      <FormField name="mobile" label="Mobile" required type="tel" {...formFieldProps} {...mobileFieldProps} />

      {extraFields?.map((field) => (
        <FormField key={field.name} {...formFieldProps} {...field} />
      ))}

      {/* <FormField name="password" type="password" label="Password" required />
      <FormField
        name="confirm_password"
        type="password"
        label="Confirm Password"
        required
      /> */}
      <Text fontWeight={500} color="inherit" {...passwordNoteProps}>
        Password will be sent via email
      </Text>
      <Button
        isLoading={isLoading || false}
        isDisabled={isLoading || false}
        mt="0.5rem"
        w="full"
        type="submit"
        {...buttonProps}
      >
        Continue
      </Button>
      {socialAuthProviders && socialAuthProviders.length > 0 && (
        <Flex
          direction="row"
          justifyContent="center"
          alignSelf="center"
          gap="1rem"
          mt="1rem"
        >
          {socialAuthProviders.map((provider) => (
            <Button
              key={provider.providerName}
              variant="ghost"
              gap="0.5rem"
              onClick={() => {
                startSocialSignIn(provider.providerName);
              }}
              {...socialButtonProps}
            >
              <Image
                src={provider.providerSvg}
                alt={provider.providerName}
                boxSize="24px"
                flexShrink={0}
              />
              {showSocialProviderLabel && (
                <Text as="span" textTransform="capitalize">
                  {provider.providerName}
                </Text>
              )}
            </Button>
          ))}
        </Flex>
      )}
      {!hideLoginLink && (
        <>
          <Divider my="1rem" />
          <Text mx="auto" fontWeight={500} {...loginLinkTextProps}>
            Already have an account?{" "}
            <Link
              href="/login"
              color="primary.500"
              _hover={{ color: "primary.700" }}
              {...loginLinkProps}
            >
              Login
            </Link>
          </Text>
        </>
      )}
    </AppForm>
  );
}

export default RegisterForm;
